/**
 * Post-Paid Request Permissions
 */
export const POST_PAID_PERMISSIONS = {
  CREATE_REQUEST: "Create_Request",
  EDIT_REQUEST: "Edit_Request",
  HOD_APPROVAL: "HOD_Approval",
  FINANCE_APPROVAL: "Finance_Approval",
  VIEW_REQUEST: "View_Request",
};

export const isSuperAdmin = (admin) => {
  if (!admin) return false;
  return !admin.role || admin.role === null || admin.role === "";
};

/**
 * Check if user has a specific permission
 */
export const hasPermission = (admin, permission) => {
  if (!admin) return false;

  if (isSuperAdmin(admin)) return true;

  const permissions = admin.permissions || [];
  return permissions.includes(permission);
};

/**
 * Check if user has any of the specified permissions
 */
export const hasAnyPermission = (admin, permissions) => {
  if (!admin) return false;

  if (isSuperAdmin(admin)) return true;

  const userPermissions = admin.permissions || [];
  return permissions.some((permission) => userPermissions.includes(permission));
};

/**
 * Check if user has all of the specified permissions
 */
export const hasAllPermissions = (admin, permissions) => {
  if (!admin) return false;

  if (isSuperAdmin(admin)) return true;

  const userPermissions = admin.permissions || [];
  return permissions.every((permission) =>
    userPermissions.includes(permission)
  );
};

/**
 * Check if user can access post-paid requests module
 * User needs at least one of: Create_Request, Edit_Request, HOD_Approval, Finance_Approval, View_Request
 */
export const canAccessPostPaidModule = (admin) => {
  const requiredPermissions = [
    POST_PAID_PERMISSIONS.CREATE_REQUEST,
    POST_PAID_PERMISSIONS.EDIT_REQUEST,
    POST_PAID_PERMISSIONS.HOD_APPROVAL,
    POST_PAID_PERMISSIONS.FINANCE_APPROVAL,
    POST_PAID_PERMISSIONS.VIEW_REQUEST,
  ];
  return hasAnyPermission(admin, requiredPermissions);
};

/**
 * Check if user can create post-paid requests
 */
export const canCreatePostPaidRequest = (admin) => {
  return hasPermission(admin, POST_PAID_PERMISSIONS.CREATE_REQUEST);
};

/**
 * Check if user can edit post-paid requests
 */
export const canEditPostPaidRequest = (admin) => {
  return hasPermission(admin, POST_PAID_PERMISSIONS.EDIT_REQUEST);
};

/**
 * Check if user can approve as HOD
 */
export const canApproveAsHOD = (admin) => {
  return hasPermission(admin, POST_PAID_PERMISSIONS.HOD_APPROVAL);
};

/**
 * Check if user can approve as Finance
 */
export const canApproveAsFinance = (admin) => {
  return hasPermission(admin, POST_PAID_PERMISSIONS.FINANCE_APPROVAL);
};

/**
 * Check if user can approve a specific request
 * Super admins have view-only access and cannot approve
 */
export const canApproveRequest = (admin, request) => {
  if (!request || !admin) return false;

  // Super admin cannot approve - view only
  if (isSuperAdmin(admin)) {
    return false;
  }

  // If both approvals are done, no one can approve
  if (request.hod_approval && request.finance_approval) {
    return false;
  }

  // If HOD approval is pending, check HOD permission
  if (!request.hod_approval && !request.finance_approval) {
    return canApproveAsHOD(admin);
  }

  // If HOD approved but Finance pending, check Finance permission
  if (request.hod_approval && !request.finance_approval) {
    return canApproveAsFinance(admin);
  }

  return false;
};

/**
 * Get the approval tier for a request
 *
 * @param {Object} request - Request object
 * @returns {string} - "hod", "finance", or "completed"
 */
export const getApprovalTier = (request) => {
  if (!request) return "completed";

  if (!request.hod_approval && !request.finance_approval) {
    return "hod";
  } else if (request.hod_approval && !request.finance_approval) {
    return "finance";
  }
  return "completed";
};

/**
 * Get user's permissions as an object for easy checking
 *
 * @param {Object} admin - Admin object from Redux store
 * @returns {Object} - Object with boolean flags for each permission
 */
export const getPostPaidPermissions = (admin) => {
  return {
    canAccess: canAccessPostPaidModule(admin),
    canCreate: canCreatePostPaidRequest(admin),
    canEdit: canEditPostPaidRequest(admin),
    canApproveHOD: canApproveAsHOD(admin),
    canApproveFinance: canApproveAsFinance(admin),
    isSuperAdmin: isSuperAdmin(admin),
  };
};
